import  { useEffect, useState } from "react";
import { useAppContext } from "../context/AppContext";
import AxiosInstance  from "../context/axiosInstance";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Building2,
  GitFork,
  Store,
  User,
  SearchX,
  Loader2,
} from "lucide-react";

import AppFooter from "./Homepage/AppFooter";


// --- MAIN ORGANISATION PAGE COMPONENT ---
export default function MyOrganisation() {
  const { accessToken, userID } = useAppContext();
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!userID || !accessToken) {
      setLoading(false);
      return;
    }
    const fetchOrgs = async () => {
      setLoading(true);
      try {
        const res = await AxiosInstance.get(
          `/organisations/?userId=${userID}`
        );
        setOrganisations(res.data || []);
      } catch (err) {
        console.error("Error fetching organisations:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrgs();
  }, [userID, accessToken]);

  return (
    <div className="bg-background text-white min-h-screen font-sans selection:bg-white selection:text-black">
       {/* Background Gradient */}
       <div className="fixed inset-0 pointer-events-none">
          <div className="absolute top-[10%] right-[20%] w-[500px] h-[500px] bg-primary-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-[10%] left-[20%] w-[600px] h-[600px] bg-secondary-500/10 rounded-full blur-[120px]" />
       </div>

      <main className="flex-grow relative z-10">
        <div className="container mx-auto px-6 py-24 md:py-32 min-h-screen">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <button
              onClick={() => navigate("/app")}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-widest mb-8"
            >
              <ArrowLeft size={16} /> Back to Dashboard
            </button>
            <h1 className="text-3xl md:text-5xl font-bold tracking-tighter mb-4 text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-500">
              My Communities
            </h1>
            <p className="text-base text-gray-400 max-w-2xl font-light">
              Drill down into the structure of your Communities to find specific
              resources.
            </p>
          </motion.div>

          <main className="mt-16">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div
                  key="loader"
                  className="flex flex-col items-center justify-center min-h-[30vh] text-gray-400"
                >
                  <Loader2 size={32} className="animate-spin mb-4 text-primary-500" />
                  <p>Loading Communities...</p>
                </motion.div>
              ) : organisations.length === 0 ? (
                <motion.div
                  key="empty"
                  className="flex flex-col items-center justify-center min-h-[30vh] text-center text-gray-400 border border-white/10 rounded-3xl p-12 bg-surface/30 backdrop-blur-md"
                >
                  <SearchX size={48} className="mb-6 opacity-50" />
                  <h2 className="text-2xl font-bold text-white mb-2">
                    No Communities Found
                  </h2>
                  <p className="max-w-md">You are not currently associated with any organisation.</p>
                </motion.div>
              ) : (
                <motion.div key="list" className="space-y-6">
                  {organisations.map((org, i) => (
                    <OrgNode key={i} org={org} navigate={navigate} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </main>
        </div>
      </main>
      <div className="container mx-auto px-6">
        <AppFooter />
      </div>
    </div>
  );
}

// --- NEW "EXPANDABLE CARD" NODE COMPONENTS ---

const cardVariants = {
  initial: { opacity: 0, height: 0 },
  animate: {
    opacity: 1,
    height: "auto",
    transition: { duration: 0.3, ease: "easeInOut" },
  },
  exit: {
    opacity: 0,
    height: 0,
    transition: { duration: 0.2, ease: "easeInOut" },
  },
};

function OrgNode({ org, navigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = (org.branches || []).length > 0;
  return (
    <div className="bg-surface/40 backdrop-blur-md border border-white/10 rounded-2xl overflow-hidden shadow-lg hover:bg-surface/60 transition-colors duration-300">
      <div
        className={`flex justify-between items-center p-6 ${
          hasChildren ? "cursor-pointer" : ""
        }`}
        onClick={() => hasChildren && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center border border-blue-500/20">
             <Building2 size={24} className="text-blue-400" />
          </div>
          <span className="text-xl font-bold text-white tracking-tight">{org.name}</span>
        </div>
        {hasChildren && (
          <ChevronDown
            size={20}
            className={`text-gray-400 transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </div>
      <AnimatePresence>
        {isOpen && hasChildren && (
          <motion.div
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="border-t border-white/5 bg-black/20"
          >
            <div className="p-4 space-y-3">
              {org.branches.map((b, i) => (
                <BranchNode key={i} branch={b} navigate={navigate} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function BranchNode({ branch, navigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = (branch.departments || []).length > 0;
  return (
    <div className="bg-white/5 rounded-xl overflow-hidden border border-white/5">
      <div
        className={`flex justify-between items-center p-4 ${
          hasChildren ? "cursor-pointer" : ""
        }`}
        onClick={() => hasChildren && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <GitFork size={18} className="text-green-400 flex-shrink-0" />
          <span className="font-semibold text-white">{branch.name}</span>
        </div>
        {hasChildren && (
          <ChevronDown
            size={18}
            className={`text-gray-500 transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </div>
      <AnimatePresence>
        {isOpen && hasChildren && (
          <motion.div
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="border-t border-white/5 pl-4 bg-black/20"
          >
            <div className="p-3 space-y-2">
              {branch.departments.map((d, i) => (
                <DepartmentNode key={i} dept={d} navigate={navigate} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DepartmentNode({ dept, navigate }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = (dept.resources || []).length > 0;
  return (
    <div className="bg-white/5 rounded-lg overflow-hidden border border-white/5">
      <div
        className={`flex justify-between items-center p-3 ${
          hasChildren ? "cursor-pointer" : ""
        }`}
        onClick={() => hasChildren && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-3">
          <Store size={16} className="text-purple-400 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-200">{dept.name}</span>
        </div>
        {hasChildren && (
          <ChevronDown
            size={16}
            className={`text-gray-600 transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        )}
      </div>
      <AnimatePresence>
        {isOpen && hasChildren && (
          <motion.div
            variants={cardVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="border-t border-white/5 pl-3 bg-black/20"
          >
            <div className="p-2 space-y-1">
              {dept.resources.map((res, i) => (
                <ResourceNode key={i} resource={res} navigate={navigate} />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ResourceNode({ resource, navigate }) {
  return (
    <div
      onClick={() => navigate(`/resource/${resource.resourceId}`)}
      className="group flex justify-between items-center p-2.5 rounded-lg cursor-pointer hover:bg-white/10 transition-colors"
    >
      <div className="flex items-center gap-2">
        <User size={14} className="text-primary-400" />
        <span className="text-xs font-medium text-gray-300 group-hover:text-white transition-colors">{resource.name}</span>
      </div>
      <ArrowRight
        size={14}
        className="text-primary-400 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-1"
      />
    </div>
  );
}
